/**
 * Parsers for the agent Task-tool family (``TaskCreate`` /
 * ``TaskUpdate`` / ``TaskGet`` / ``TaskList``) tool *results*.
 *
 * The SDK's built-in Task tools return human-readable PLAIN TEXT, not
 * JSON. Captured verbatim from the live tools:
 *
 *   - ``TaskCreate`` -> ``Task #1 created successfully: <subject>``
 *   - ``TaskUpdate`` -> ``Updated task #1 status, owner`` (changed
 *     field names; ``Updated task #1 deleted`` on delete)
 *   - ``TaskList``   -> one line per task,
 *     ``#1 [pending] <subject>`` with optional `` (<owner>)`` and
 *     `` [blocked by #2, #3]`` suffixes
 *   - ``TaskGet``    -> a block,
 *     ``Task #1: <subject>`` / ``Status: <status>`` /
 *     ``Description: <desc>`` (continuation lines are raw, unprefixed)
 *     / ``Blocked by: #2, #3``
 *
 * Earlier code ``JSON.parse``'d these strings, which always threw and
 * silently dropped the task into a no-op -- the activity chip never
 * appeared and task rows rendered the bare id twice. Both the reducer
 * (chip / plan state) and the expanded-body renderer parse through
 * here so the real text format has exactly one home.
 *
 * The parsed payloads are normalized to the shape the consumers
 * already expect (``{ task }`` / ``{ tasks }`` / ``{ taskId }``) so the
 * downstream merge / render logic stays unchanged.
 */

/** A task as recovered from a Task-tool text result. */
export interface ParsedTask {
  id: string;
  subject: string;
  status: string;
  description?: string;
  owner?: string;
  blockedBy?: string[];
  blocks?: string[];
}

/** Normalized Task-tool result payload, keyed like the old JSON shape. */
export interface ParsedTaskResult {
  task?: ParsedTask;
  tasks?: ParsedTask[];
  taskId?: string;
  success?: boolean;
}

// ``Task #12 created successfully: Wire the reducer``
const CREATE_RE = /^Task #(\d+) created(?: successfully)?:\s*(.+)$/;
// ``Updated task #12 status, owner`` / ``Updated task #12 deleted``
const UPDATE_RE = /^Updated task #(\d+)(?:\s+(.+))?$/;
// ``#12 [in_progress] Wire the reducer (claude)``
// The trailing ``(owner)`` group is inherently ambiguous: a row with no
// owner whose subject happens to END in parentheses ("fix flaky test
// (CI)") parses the paren tail as the owner. The wire format gives no
// way to tell them apart, so we accept the misread — a wrong owner chip
// — rather than dropping the row.
const LIST_RE = /^#(\d+)\s+\[([^\]]+)\]\s+(.+?)(?:\s+\(([^)]+)\))?$/;
// `` [blocked by #2, #3]`` — appended to a TaskList row for blocked
// tasks. Stripped before LIST_RE so it can never be swallowed into the
// subject (or misread as an owner), wherever it sits relative to the
// owner suffix.
const LIST_BLOCKED_RE = /\s+\[blocked by ([^\]]+)\]/;
// ``Task #12: Wire the reducer``
const GET_HEAD_RE = /^Task #(\d+):\s*(.+)$/;

/**
 * Parse a Task-tool text result into the normalized payload its
 * consumers expect, or ``null`` when the text doesn't match the
 * tool's format (a malformed / unexpected result we'd rather ignore
 * than render garbage from).
 *
 * :param name: The tool name (``TaskCreate`` / ``TaskUpdate`` / …).
 * :param text: The raw text result.
 * :return: The normalized payload, or ``null`` when unparseable.
 */
export function parseTaskResultText(name: string, text: string): ParsedTaskResult | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  switch (name) {
    case 'TaskCreate':
      return parseCreate(trimmed);
    case 'TaskUpdate':
      return parseUpdate(trimmed);
    case 'TaskList':
      return parseList(trimmed);
    case 'TaskGet':
      return parseGet(trimmed);
    default:
      return null;
  }
}

function parseCreate(text: string): ParsedTaskResult | null {
  const match = CREATE_RE.exec(text.split('\n')[0]?.trim() ?? '');
  if (!match) return null;
  return { task: { id: match[1], subject: match[2].trim(), status: 'pending' } };
}

function parseUpdate(text: string): ParsedTaskResult | null {
  const match = UPDATE_RE.exec(text.split('\n')[0]?.trim() ?? '');
  if (!match) return null;
  // The changed-field names after the id are not surfaced: the body
  // renderer derives the change description from the call *input*,
  // which carries the actual values, not just names.
  return { taskId: match[1], success: true };
}

function parseList(text: string): ParsedTaskResult | null {
  const tasks: ParsedTask[] = [];
  for (const rawLine of text.split('\n')) {
    let line = rawLine.trim();
    let blockedBy: string[] = [];
    const blocked = LIST_BLOCKED_RE.exec(line);
    if (blocked) {
      blockedBy = parseTaskIdList(blocked[1]);
      line = `${line.slice(0, blocked.index)}${line.slice(blocked.index + blocked[0].length)}`.trim();
    }
    const match = LIST_RE.exec(line);
    if (!match) continue;
    const task: ParsedTask = { id: match[1], subject: match[3].trim(), status: match[2].trim() };
    if (match[4]) task.owner = match[4].trim();
    if (blockedBy.length > 0) task.blockedBy = blockedBy;
    tasks.push(task);
  }
  if (tasks.length === 0) {
    // Zero parsed rows is only an authoritative "clear the plan" when the text
    // is the live empty-list sentinel (``No tasks found``). Any OTHER non-empty
    // text that matched no rows — a future format change, an informational
    // line — is unparseable: return null so the reducer IGNORES it rather than
    // treating it as an empty list and wiping the current plan/chip. (Fully
    // blank text never reaches here; the caller's empty-input guard returns
    // null first.)
    return /^no tasks\b/i.test(text) ? { tasks: [] } : null;
  }
  return { tasks };
}

function parseGet(text: string): ParsedTaskResult | null {
  const lines = text.split('\n');
  const head = GET_HEAD_RE.exec(lines[0]?.trim() ?? '');
  if (!head) return null;
  const task: ParsedTask = { id: head[1], subject: head[2].trim(), status: 'pending' };
  // Multi-line descriptions arrive RAW: only the first line carries the
  // ``Description:`` label; continuation lines are unprefixed. Accumulate
  // them until the next known label instead of label-matching every line
  // independently — otherwise only the first description line survives,
  // and a continuation line that happens to start with ``Status:`` would
  // overwrite the real status. (A continuation line that genuinely
  // starts with a label is indistinguishable from the label — inherent
  // wire-format ambiguity we accept.)
  const descriptionLines: string[] = [];
  let inDescription = false;
  for (const line of lines.slice(1)) {
    const trimmed = line.trim();
    const value = (label: string) => trimmed.slice(label.length).trim();
    if (trimmed.startsWith('Status:')) {
      inDescription = false;
      task.status = value('Status:') || task.status;
    } else if (trimmed.startsWith('Description:')) {
      inDescription = true;
      descriptionLines.push(value('Description:'));
    } else if (trimmed.startsWith('Owner:')) {
      inDescription = false;
      const owner = value('Owner:');
      if (owner) task.owner = owner;
    } else if (trimmed.startsWith('Blocked by:')) {
      inDescription = false;
      const ids = parseTaskIdList(value('Blocked by:'));
      if (ids.length > 0) task.blockedBy = ids;
    } else if (trimmed.startsWith('Blocks:')) {
      inDescription = false;
      const ids = parseTaskIdList(value('Blocks:'));
      if (ids.length > 0) task.blocks = ids;
    } else if (inDescription) {
      descriptionLines.push(trimmed);
    }
  }
  const description = descriptionLines.join('\n').trim();
  if (description) task.description = description;
  return { task };
}

/**
 * Normalize a Task-tool result into the ``{ task } / { tasks } /
 * { taskId }`` payload the reducer and the body renderers consume.
 *
 * One home for the policy both share: honor a structured object
 * (future-proofing for an SDK that ever ships JSON) and otherwise parse
 * the real plain-text format. Callers supply their own extraction of
 * the two inputs since their result-block utilities differ.
 *
 * :param name: The tool name (``TaskCreate`` / ``TaskUpdate`` / …).
 * :param structured: The result content when it is already an object.
 * :param text: The raw text result, or ``null``.
 * :return: The normalized payload, or ``null`` when unparseable.
 */
export function normalizeTaskResultPayload(
  name: string,
  structured: unknown,
  text: string | null
): Record<string, unknown> | null {
  if (structured && typeof structured === 'object' && !Array.isArray(structured)) {
    return structured as Record<string, unknown>;
  }
  if (!text) return null;
  return parseTaskResultText(name, text) as Record<string, unknown> | null;
}

/** Split ``#2, #3`` / ``2, 3`` id lists into bare id strings. */
function parseTaskIdList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim().replace(/^#/, ''))
    .filter(Boolean);
}
